import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaSatellite } from 'react-icons/fa';
import SectionHeader from '../components/SectionHeader';
import Modal from '../components/Modal';
import AsciiDonut from '../components/AsciiDonut';
import { experience, education, certifications } from '../data/portfolio';
import styles from './About.module.css';

function About() {
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(null);

  const openModal = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setModalType(null);
  };

  return (
    <div className={styles.about}>
      <AsciiDonut />
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.imageContainer}>
            <img 
              src="/JoaoLopesPhoto.jpg" 
              alt="João Lopes" 
              className={styles.profileImage}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/300x300/111/00ff80?text=JL';
              }}
            />
          </div>
          <div className={styles.intro}>
            <h1 className={styles.greeting}>Hi, I'm <span className={styles.name}>João Lopes</span></h1>
            <p className={styles.tagline}>System & Cybersecurity Engineer</p>
            <div className={styles.roleContext}>
              <span className={styles.roleDetail}>
                <FaSatellite aria-hidden="true" />
                CGI · Space · IRIS²
              </span>
              <span className={styles.roleDetail}>
                <FaMapMarkerAlt aria-hidden="true" />
                Rotterdam, Netherlands
              </span>
            </div>
            <p className={styles.description}>
              I joined CGI on 1 April 2026, working in the space sector on the IRIS² programme.
              My background spans systems integration, full-stack development and DevOps,
              including microservices and large-scale R&D projects. I enjoy connecting
              these disciplines to solve complex engineering problems and build useful
              digital products.
            </p>
            <div className={styles.actions}>
              <a 
                href="/CV_JoaoLopes.pdf" 
                download 
                className={styles.cvButton}
              >
                Download CV
              </a>
              <Link to="/contact" className={styles.contactButton}>
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SectionHeader title="Experience" subtitle="Professional journey and key projects" />
      <div className={styles.cardGrid}>
        {experience.map(exp => (
          <button
            type="button"
            key={exp.id} 
            className={`${styles.card} ${styles.clickable} ${exp.current ? styles.currentCard : ''}`}
            onClick={() => openModal(exp, 'experience')}
            aria-haspopup="dialog"
          >
            {exp.current && <span className={styles.currentBadge}>Current role</span>}
            <span className={styles.cardTitle}>{exp.position}</span>
            <span className={styles.organization}>{exp.organization}</span>
            <span className={styles.period}>{exp.period}</span>
            <span className={styles.cardDescription}>{exp.description}</span>
            <span className={styles.clickHint}>Explore this role →</span>
          </button>
        ))}
      </div>

      <SectionHeader title="Education & Certifications" subtitle="Academic background and achievements" />
      <div className={styles.cardGrid}>
        {education.map(edu => (
          <button
            type="button"
            key={edu.id} 
            className={`${styles.card} ${styles.clickable}`}
            onClick={() => openModal(edu, 'education')}
            aria-haspopup="dialog"
          >
            <span className={styles.cardTitle}>{edu.degree}</span>
            <span className={styles.organization}>{edu.institution}</span>
            <span className={styles.period}>{edu.period}</span>
            <span className={styles.cardDescription}>{edu.details}</span>
            <span className={styles.clickHint}>Explore this qualification →</span>
          </button>
        ))}
      </div>

      <div className={styles.certifications}>
        {certifications.map(cert => (
          <button
            type="button"
            key={cert.id} 
            className={`${styles.certCard} ${styles.clickable}`}
            onClick={() => openModal(cert, 'certification')}
            aria-haspopup="dialog"
          >
            <span className={styles.certTitle}>{cert.name}</span>
            <span className={styles.issuer}>{cert.issuer} • {cert.year}</span>
            <span className={styles.certDescription}>{cert.description}</span>
            <span className={styles.clickHint}>Explore this certification →</span>
          </button>
        ))}
      </div>

      <Modal
        isOpen={selectedItem !== null}
        onClose={closeModal}
        title={selectedItem?.position || selectedItem?.degree || selectedItem?.name}
      >
        {selectedItem && modalType === 'experience' && (
          <div>
            <h2>{selectedItem.position}</h2>
            <p><strong>{selectedItem.organization}</strong></p>
            <p style={{ color: 'var(--color-accent)', marginBottom: '20px' }}>{selectedItem.period}</p>
            
            <p>{selectedItem.fullDescription}</p>

            <h3>{selectedItem.current ? 'Areas of Focus' : 'Skills Developed'}</h3>
            <ul>
              {selectedItem.skillsDeveloped.map((skill, idx) => (
                <li key={idx}>{skill}</li>
              ))}
            </ul>
          </div>
        )}

        {selectedItem && modalType === 'education' && (
          <div>
            <h2>{selectedItem.degree}</h2>
            <p><strong>{selectedItem.institution}</strong></p>
            <p style={{ color: 'var(--color-accent)', marginBottom: '20px' }}>{selectedItem.period}</p>
            
            <p>{selectedItem.fullDescription}</p>

            {selectedItem.dissertation && (
              <>
                <h3>{selectedItem.dissertation.title}</h3>
                <p>{selectedItem.dissertation.description}</p>
              </>
            )}

            <h3>Skills Developed</h3>
            {selectedItem.skillsDeveloped.map((category, idx) => (
              <div key={idx}>
                <h4>{category.category}</h4>
                <ul>
                  {category.skills.map((skill, skillIdx) => (
                    <li key={skillIdx}>{skill}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {selectedItem && modalType === 'certification' && (
          <div>
            <h2>{selectedItem.name}</h2>
            <p><strong>{selectedItem.issuer}</strong> • {selectedItem.year}</p>
            
            <p>{selectedItem.fullDescription}</p>

            <h3>Skills Developed</h3>
            <ul>
              {selectedItem.skillsDeveloped.map((skill, idx) => (
                <li key={idx}>{skill}</li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      <div className={styles.hobbiesPreview}>
        <p>
          Beyond engineering, my interests include rugby, retro game development and
          sharing technical knowledge.
          <Link to="/hobbies" className={styles.learnMore}> Learn more about my hobbies →</Link>
        </p>
      </div>
    </div>
  );
}

export default About;
